import { useState, useRef, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react'
import { Play, Pause, Volume2, VolumeX } from 'lucide-react'
import { STEM_COLORS, formatDuration } from '../lib/utils'

interface AudioPlayerProps {
  name: string
  src: string
  onPlayStateChange?: (isPlaying: boolean) => void
}

export interface AudioPlayerRef {
  play: () => void
  pause: () => void
  stop: () => void
  seek: (time: number) => void
  getIsPlaying: () => boolean
  getCurrentTime: () => number
  getDuration: () => number
}

const STEM_HEX: Record<string, string> = {
  vocals: '#ec4899',
  bass: '#f97316',
  drums: '#eab308',
  guitar: '#22c55e',
  piano: '#3b82f6',
  other: '#a855f7'
}

const waveformCache = new Map<string, number[]>()

export const AudioPlayer = forwardRef<AudioPlayerRef, AudioPlayerProps>(function AudioPlayer(
  { name, src, onPlayStateChange },
  ref
) {
  const audioRef = useRef<HTMLAudioElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [volume, setVolume] = useState(0.8)
  const [isMuted, setIsMuted] = useState(false)
  const [waveformData, setWaveformData] = useState<number[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isVisible, setIsVisible] = useState(false)

  const stemColorClass = STEM_COLORS[name] || 'bg-gray-500'
  const stemColorHex = STEM_HEX[name] || '#6b7280'

  // Expose methods via ref for synchronized playback
  useImperativeHandle(ref, () => ({
    play: () => {
      audioRef.current?.play()
      setIsPlaying(true)
    },
    pause: () => {
      audioRef.current?.pause()
      setIsPlaying(false)
    },
    stop: () => {
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current.currentTime = 0
      }
      setIsPlaying(false)
      setCurrentTime(0)
    },
    seek: (time: number) => {
      if (audioRef.current) {
        audioRef.current.currentTime = time
        setCurrentTime(time)
      }
    },
    getIsPlaying: () => isPlaying,
    getCurrentTime: () => currentTime,
    getDuration: () => duration
  }), [isPlaying, currentTime, duration])

  useEffect(() => {
    if (!containerRef.current) return
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
          observer.disconnect()
        }
      },
      { threshold: 0.1 }
    )
    
    observer.observe(containerRef.current)
    return () => observer.disconnect()
  }, [])

  const generateWaveform = useCallback(async () => {
    if (waveformCache.has(src)) {
      setWaveformData(waveformCache.get(src)!)
      setIsLoading(false)
      return
    }

    try {
      const response = await fetch(src)
      const arrayBuffer = await response.arrayBuffer()
      const audioContext = new AudioContext()
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer)
      
      const rawData = audioBuffer.getChannelData(0)
      const samples = 80
      const blockSize = Math.floor(rawData.length / samples)
      const filteredData: number[] = []
      
      for (let i = 0; i < samples; i++) {
        let sum = 0
        const start = i * blockSize
        const end = Math.min(start + blockSize, rawData.length)
        for (let j = start; j < end; j++) {
          sum += Math.abs(rawData[j])
        }
        filteredData.push(sum / (end - start))
      }
      
      const maxVal = Math.max(...filteredData)
      const normalized = filteredData.map(v => Math.max(0.05, v / maxVal))
      
      waveformCache.set(src, normalized)
      setWaveformData(normalized)
      setIsLoading(false)
      audioContext.close()
    } catch (err) {
      console.error('Failed to generate waveform:', err)
      setIsLoading(false)
    }
  }, [src])

  useEffect(() => {
    if (isVisible) {
      generateWaveform()
    }
  }, [isVisible, generateWaveform])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas || waveformData.length === 0) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const dpr = window.devicePixelRatio || 1
    const rect = canvas.getBoundingClientRect()
    canvas.width = rect.width * dpr
    canvas.height = rect.height * dpr
    ctx.scale(dpr, dpr)

    ctx.clearRect(0, 0, rect.width, rect.height)
    
    const barWidth = rect.width / waveformData.length
    const progressPercent = duration > 0 ? currentTime / duration : 0
    const progressX = rect.width * progressPercent

    waveformData.forEach((value, index) => {
      const x = index * barWidth
      const barHeight = Math.max(value * (rect.height - 6), 2)
      const y = (rect.height - barHeight) / 2

      if (x < progressX) {
        ctx.fillStyle = stemColorHex
      } else {
        ctx.fillStyle = 'rgba(255, 255, 255, 0.2)'
      }

      ctx.beginPath()
      ctx.roundRect(x + 1, y, barWidth - 2, barHeight, 1)
      ctx.fill()
    })
  }, [waveformData, currentTime, duration, stemColorHex])

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    const handleTimeUpdate = () => setCurrentTime(audio.currentTime)
    const handleLoadedMetadata = () => setDuration(audio.duration)
    const handleEnded = () => setIsPlaying(false)

    audio.addEventListener('timeupdate', handleTimeUpdate)
    audio.addEventListener('loadedmetadata', handleLoadedMetadata)
    audio.addEventListener('ended', handleEnded)

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate)
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata)
      audio.removeEventListener('ended', handleEnded)
    }
  }, [])

  const togglePlay = (e: React.MouseEvent) => {
    e.stopPropagation()
    const audio = audioRef.current
    if (!audio) return

    const newState = !isPlaying
    if (newState) {
      audio.play()
    } else {
      audio.pause()
    }
    setIsPlaying(newState)
    onPlayStateChange?.(newState)
  }

  const handleSeek = (e: React.MouseEvent<HTMLCanvasElement>) => {
    e.stopPropagation()
    const audio = audioRef.current
    const canvas = canvasRef.current
    if (!audio || !canvas) return

    const rect = canvas.getBoundingClientRect()
    const x = e.clientX - rect.left
    const percent = x / rect.width
    const time = percent * duration
    
    audio.currentTime = time
    setCurrentTime(time)
  }

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation()
    const audio = audioRef.current
    if (!audio) return

    const vol = parseFloat(e.target.value)
    audio.volume = vol
    setVolume(vol)
    setIsMuted(vol === 0)
  }

  const toggleMute = (e: React.MouseEvent) => {
    e.stopPropagation()
    const audio = audioRef.current
    if (!audio) return

    if (isMuted) {
      audio.volume = volume || 0.8
      setIsMuted(false)
    } else {
      audio.volume = 0
      setIsMuted(true)
    }
  }

  const glowClass = `stem-glow-${name}`

  return (
    <div
      ref={containerRef}
      className={`bg-surface-800/50 rounded-xl p-3 border border-white/5 transition-all duration-300 hover:border-white/10 ${isPlaying ? glowClass : ''}`}
      onClick={(e) => e.stopPropagation()}
    >
      <audio ref={audioRef} src={src} preload="metadata" />
      
      <div className="flex items-center gap-3">
        <button
          onClick={togglePlay}
          className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-200 flex-shrink-0 ${stemColorClass} hover:scale-105 active:scale-95`}
          style={{ boxShadow: isPlaying ? `0 0 20px ${stemColorHex}40` : 'none' }}
        >
          {isPlaying ? (
            <Pause className="w-5 h-5 text-white" />
          ) : (
            <Play className="w-5 h-5 text-white ml-0.5" />
          )}
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-sm font-medium capitalize text-white">{name}</span>
            <span className="text-xs text-gray-500 tabular-nums">
              {formatDuration(currentTime)} / {formatDuration(duration)}
            </span>
          </div>
          
          <div className="relative h-10 bg-surface-900/50 rounded-lg overflow-hidden cursor-pointer">
            {isLoading ? (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="flex gap-0.5 items-end h-6">
                  {[...Array(16)].map((_, i) => (
                    <div
                      key={i}
                      className="w-1 bg-surface-700 rounded-full animate-pulse"
                      style={{ 
                        height: `${20 + Math.sin(i * 0.5) * 30 + 30}%`,
                        animationDelay: `${i * 50}ms`
                      }}
                    />
                  ))}
                </div>
              </div>
            ) : (
              <canvas
                ref={canvasRef}
                className="w-full h-full"
                onClick={handleSeek}
              />
            )}
            <div 
              className="absolute top-0 bottom-0 w-0.5 bg-white pointer-events-none"
              style={{ 
                left: `${duration > 0 ? (currentTime / duration) * 100 : 0}%`,
                boxShadow: `0 0 6px ${stemColorHex}`
              }}
            />
          </div>
        </div>

        <div className="flex items-center gap-1.5 flex-shrink-0">
          <button 
            onClick={toggleMute} 
            className="text-gray-500 hover:text-white transition-colors p-1"
          >
            {isMuted ? (
              <VolumeX className="w-4 h-4" />
            ) : (
              <Volume2 className="w-4 h-4" />
            )}
          </button>
          <input
            type="range"
            min="0"
            max="1"
            step="0.1"
            value={isMuted ? 0 : volume}
            onChange={handleVolumeChange}
            className="w-14 h-1 bg-surface-700 rounded-full appearance-none cursor-pointer"
            style={{
              background: `linear-gradient(to right, ${stemColorHex} 0%, ${stemColorHex} ${(isMuted ? 0 : volume) * 100}%, rgb(51, 65, 85) ${(isMuted ? 0 : volume) * 100}%, rgb(51, 65, 85) 100%)`
            }}
          />
        </div>
      </div>
    </div>
  )
})
